import React, { useState, useEffect, useCallback, useRef } from 'react';
import { emailLogsService } from '../services/api';
import { fmtDateTime } from '../utils/formatters';
import { TONE_BADGE, TONE_TEXT } from '../utils/statusColors';
import { rotuloDoTipo, TIPOS_DO_FILTRO, statusDoEnvio, barraDoEnvio, barraDoProblema, caminhoDoEnvio, STATUS_NAO_CHEGOU, clienteDaLinha, enviadoPorTexto, VISOES_DO_PUBLICO, Publico } from '../utils/emailLogs';
import { CartaoDoTopo, barraDoEstado } from './shared/lista/PecasDaLista';
import { campo, useEstadoDaCarga, EstadoDaLista, A_PROPRIA_LISTA_DIZ_O_VAZIO, textos } from '../design';

const Icon = ({ name, className = 'w-5 h-5' }: { name: string; className?: string }) => {
  const icons: any = {
    mail: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' /></svg>,
    chart: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' /></svg>,
    chevronLeft: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' /></svg>,
    chevronRight: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' /></svg>,
    refresh: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' /></svg>,
  };
  return icons[name] || null;
};

interface EmailLog {
  id: number;
  entityType: string | null;
  entityId: number | null;
  recipientEmail: string;
  subject: string;
  fromEmail: string | null;
  purpose: string | null;
  transportType: string | null;
  sendgridMessageId: string | null;
  status: string;
  sentAt: string;
  deliveredAt: string | null;
  openedAt: string | null;
  openCount: number;
  clickedAt: string | null;
  bouncedAt: string | null;
  bounceReason: string | null;
  // B1/C1 (backend do Bastião, 27/10/2026): o cliente pelo registro de origem, uma linha por envio, e quem enviou.
  interno?: boolean;
  clientId?: number | null;
  clientName: string | null;
  clientTradeName?: string | null;
  clientCompanyName?: string | null;
  origemDoCliente?: string | null;
  clientesDoEmail?: number | null;
  enviadoPor?: { tipo: 'pessoa' | 'automatico' | 'nao_registrado'; nome: string | null; rotina: string | null } | null;
}

interface Stats {
  total: number;
  sent: number;
  delivered: number;
  opened: number;
  bounced: number;
  failed: number;
  deliveredPct: number;
  openedPct: number;
}

interface PaginatedResult {
  data: EmailLog[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export default function EmailLogsTab() {
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [meta, setMeta] = useState<PaginatedResult['meta'] | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  // A1: a visão padrão é a dos clientes; os alertas internos (36% dos envios em 30 dias) ficam a um clique.
  const [publico, setPublico] = useState<Publico>('clientes');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const searchTimeout = useRef<any>(null);

  const handleSearchInput = (value: string) => {
    setSearchInput(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => setSearch(value), 400);
  };

  // D7 (11/10/2026): a tela que é a PROVA do que saiu dizia "Nenhum email encontrado" quando não leu, e o caminho
  // natural era reenviar. E a resposta de um filtro velho podia cobrir a de um filtro novo: cada leitura leva a chave do
  // recorte, e a que chega fora de hora é descartada.
  const carga = useEstadoDaCarga();
  const rodarCarga = carga.rodar;
  const recorteAtual = useRef('');
  const [recorteDaLista, setRecorteDaLista] = useState<string | null>(null);
  const chaveDoRecorte = JSON.stringify([page, publico, statusFilter, entityTypeFilter, startDate, endDate, search]);
  recorteAtual.current = chaveDoRecorte;
  const loadData = useCallback(async () => {
    setLoading(true);
    const chave = JSON.stringify([page, publico, statusFilter, entityTypeFilter, startDate, endDate, search]);
    await rodarCarga(async () => {
      // D1: os cartões recebem EXATAMENTE os filtros da lista (menos a página) — contam o que a lista mostra.
      const filtros: any = {};
      if (publico) filtros.publico = publico;
      if (statusFilter) filtros.status = statusFilter;
      if (entityTypeFilter) filtros.entityType = entityTypeFilter;
      if (startDate) filtros.startDate = startDate;
      if (endDate) filtros.endDate = endDate;
      if (search) filtros.search = search;

      const [logsResult, statsResult] = await Promise.all([
        emailLogsService.getAll({ ...filtros, page, limit: 50 }),
        emailLogsService.getStats(filtros),
      ]);

      if (recorteAtual.current !== chave) return;
      setLogs(logsResult.data || []);
      setMeta(logsResult.meta || null);
      setStats(statsResult);
      setRecorteDaLista(chave);
    });
    setLoading(false);
  }, [page, publico, statusFilter, entityTypeFilter, startDate, endDate, search, rodarCarga]);
  const listaValida = recorteDaLista === chaveDoRecorte;
  const n = (v: number | string) => (listaValida ? v : textos.numeroSemFonte);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [publico, statusFilter, entityTypeFilter, startDate, endDate, search]);

  // T1 (26/10/2026): só problema é colorido; o caminho normal é neutro. Regras em utils/emailLogs.ts.
  const getStatusBadge = (status: string) => {
    const { rotulo, tom } = statusDoEnvio(status);
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TONE_BADGE[tom]}`} data-status-do-envio={tom}>
        {rotulo}
      </span>
    );
  };

  const getEntityLabel = (type: string | null) => rotuloDoTipo(type);

  // E1 (27/10/2026): o caminho em texto, no lugar das três bolinhas sem legenda; o problema traz o motivo.
  const renderCaminho = (log: EmailLog) => {
    const { texto, tom } = caminhoDoEnvio(log);
    // O motivo do provedor chega a 300 caracteres (RBL com URL e IP): duas linhas na lista, o texto inteiro no title.
    return <span className={`text-sm line-clamp-2 break-words ${tom === 'neutral' ? 'text-slate-300' : TONE_TEXT[tom]}`} title={texto} data-caminho={tom}>{texto}</span>;
  };

  const buildTooltip = (log: EmailLog) => {
    const parts: string[] = [];
    parts.push(`Enviado: ${fmtDateTime(log.sentAt)}`);
    if (log.deliveredAt) parts.push(`Entregue: ${fmtDateTime(log.deliveredAt)}`);
    if (log.openedAt) parts.push(`Aberto: ${fmtDateTime(log.openedAt)}${log.openCount > 1 ? ` (${log.openCount}x)` : ''}`);
    if (log.clickedAt) parts.push(`Clicado: ${fmtDateTime(log.clickedAt)}`);
    if (log.bouncedAt) parts.push(`Rejeitado: ${fmtDateTime(log.bouncedAt)}`);
    if (log.bounceReason) parts.push(`Motivo: ${log.bounceReason}`);
    return parts.join('\n');
  };

  return (
    <div className="space-y-6">
      {/* Os cartões (A2, T1): neutros, número branco; Rejeitados e Falhas ganham a barra vermelha só acima de zero. O
          percentual de entregues vem do backend (o 168% é conserto dele: soma os abertos duas vezes), sem remendo aqui. */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-cartoes-dos-envios>
          <CartaoDoTopo rotulo="Enviados" valor={n(stats.total)} />
          <CartaoDoTopo rotulo="Entregues" valor={<>{n(stats.deliveredPct)}%</>} sub={<>{n(stats.delivered)} de {n(stats.total)}</>} />
          <CartaoDoTopo rotulo="Abertos" valor={<>{n(stats.openedPct)}%</>} sub={<>{n(stats.opened)} de {n(stats.total)}</>} />
          {/* E1: o cartão vira filtro do mesmo conjunto que ele conta (rejeitado, descartado, spam); de novo, desfaz. */}
          <CartaoDoTopo
            rotulo="Rejeitados"
            valor={n(stats.bounced)}
            barra={listaValida ? barraDoProblema(stats.bounced) : null}
            ativo={statusFilter === STATUS_NAO_CHEGOU}
            onClick={() => setStatusFilter((f) => (f === STATUS_NAO_CHEGOU ? '' : STATUS_NAO_CHEGOU))}
            title="Mostrar os que não chegaram, com o motivo"
          />
          <CartaoDoTopo rotulo="Falhas" valor={n(stats.failed)} barra={listaValida ? barraDoProblema(stats.failed) : null} />
        </div>
      )}

      {/* Filters + Table Card */}
      <div className="bg-central-cartao rounded-central border border-central-borda overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-central-borda flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div>
              <h3 className="text-white font-semibold">Envios de e-mail</h3>
              <p className="text-slate-400 text-sm">Histórico de todos os e-mails enviados pelo sistema</p>
            </div>
          </div>
          <button
            onClick={loadData}
            className="p-2 text-slate-400 hover:text-white hover:bg-central-elevado rounded-lg transition-colors"
            title="Recarregar"
          >
            <Icon name="refresh" className="w-4 h-4" />
          </button>
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-central-borda flex flex-wrap items-center gap-3">
          <div role="group" aria-label="Para quem" className="inline-flex rounded-lg border border-central-borda overflow-hidden" data-visao-do-publico={publico || 'todos'}>
            {VISOES_DO_PUBLICO.map((v) => (
              <button
                key={v.rotulo}
                type="button"
                aria-pressed={publico === v.valor}
                onClick={() => setPublico(v.valor)}
                className={`px-3 py-2 text-sm ${publico === v.valor ? 'bg-central-elevado text-white font-medium' : 'text-slate-400 hover:text-white'}`}
              >
                {v.rotulo}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => handleSearchInput(e.target.value)}
            className={`text-sm rounded-lg px-3 py-2 min-w-[240px] ${campo}`}
            placeholder="Buscar por email, cliente, CNPJ ou assunto..."
          />

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={`text-sm rounded-lg px-3 py-2 ${campo}`}
          >
            <option value="">Todos os status</option>
            <option value="sent">Enviado</option>
            <option value="delivered">Entregue</option>
            <option value="opened">Aberto</option>
            <option value={STATUS_NAO_CHEGOU}>Não chegou (rejeitado, descartado, spam)</option>
            <option value="bounced">Rejeitado</option>
            <option value="failed">Falhou</option>
            <option value="deferred">Atrasado</option>
            <option value="dropped">Descartado</option>
          </select>

          <select
            value={entityTypeFilter}
            onChange={(e) => setEntityTypeFilter(e.target.value)}
            className={`text-sm rounded-lg px-3 py-2 ${campo}`}
          >
            <option value="">Todos os tipos</option>
            {TIPOS_DO_FILTRO.map((t) => <option key={t.valor} value={t.valor}>{t.rotulo}</option>)}
          </select>

          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className={`text-sm rounded-lg px-3 py-2 ${campo}`}
            placeholder="Data inicio"
          />

          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className={`text-sm rounded-lg px-3 py-2 ${campo}`}
            placeholder="Data fim"
          />

          {(statusFilter || entityTypeFilter || startDate || endDate || search) && (
            <button
              onClick={() => { setStatusFilter(''); setEntityTypeFilter(''); setStartDate(''); setEndDate(''); setSearch(''); setSearchInput(''); }}
              className="text-xs text-slate-400 hover:text-white px-2 py-1"
            >
              Limpar filtros
            </button>
          )}
        </div>

        {/* Table */}
        <EstadoDaLista carga={listaValida ? carga : { ...carga, estado: carga.estado === 'erro' ? 'erro' : 'carregando', atualizadoEm: null }} temDados={listaValida} oQue='os envios (não quer dizer que nada saiu)' onTentarDeNovo={loadData} vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}>
        {loading && !listaValida ? (
          <div className="p-12 text-center">
            <div className="animate-spin w-8 h-8 border-2 border-slate-400 border-t-transparent rounded-full mx-auto mb-3" />
            <p className="text-slate-400 text-sm">Carregando logs...</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="p-12 text-center">
            <Icon name="mail" className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">Nenhum email encontrado</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-xs text-slate-400 uppercase border-b border-central-borda">
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-left">Tipo</th>
                    <th className="px-4 py-3 text-left">Cliente</th>
                    <th className="px-4 py-3 text-left">Destinatário</th>
                    <th className="px-4 py-3 text-left">Assunto</th>
                    <th className="px-4 py-3 text-left">Enviado por</th>
                    <th className="px-4 py-3 text-center">Data</th>
                    <th className="px-4 py-3 text-left">Caminho</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-central-borda">
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-central-elevado transition-colors" title={buildTooltip(log)}>
                      <td className={`px-4 py-3 ${barraDoEstado(barraDoEnvio(log.status))}`}>
                        {getStatusBadge(log.status)}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-300">
                          {getEntityLabel(log.entityType)}
                        </span>
                        {log.entityId && (
                          <span className="ml-1 text-xs text-slate-500">#{log.entityId}</span>
                        )}
                      </td>
                      <td className="px-4 py-3" data-cliente-da-linha>
                        {(() => {
                          const c = clienteDaLinha(log);
                          return (
                            <div>
                              <p className={`text-sm ${c.interno || c.texto === '—' ? 'text-slate-500' : 'font-medium text-white'}`}>{c.texto}</p>
                              {c.sub && <p className="text-xs text-slate-500">{c.sub}</p>}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-white truncate max-w-[200px] inline-block">
                          {log.recipientEmail}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-300 truncate max-w-[300px] inline-block">
                          {log.subject}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap" data-enviado-por>
                        <span className={`text-sm ${log.enviadoPor?.tipo === 'pessoa' ? 'text-slate-300' : 'text-slate-500'}`}>{enviadoPorTexto(log.enviadoPor)}</span>
                      </td>
                      <td className="px-4 py-3 text-center whitespace-nowrap">
                        <span className="text-sm text-slate-400">{fmtDateTime(log.sentAt)}</span>
                      </td>
                      <td className="px-4 py-3 max-w-[280px]">
                        {renderCaminho(log)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {meta && (
              <div className="p-4 border-t border-central-borda flex items-center justify-between">
                <span className="text-sm text-slate-400">
                  Mostrando {((meta.page - 1) * meta.limit) + 1}-{Math.min(meta.page * meta.limit, meta.total)} de {meta.total}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={!meta.hasPrevPage}
                    className="p-2 text-slate-400 hover:text-white hover:bg-central-elevado rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <Icon name="chevronLeft" className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-slate-300 px-2">
                    {meta.page} / {meta.totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => p + 1)}
                    disabled={!meta.hasNextPage}
                    className="p-2 text-slate-400 hover:text-white hover:bg-central-elevado rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <Icon name="chevronRight" className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
        </EstadoDaLista>
      </div>
    </div>
  );
}
